import { SEO } from '../../../utils/seo';
import GlobalNavbar from '../../../components/feature/GlobalNavbar';
import Footer from '../../../components/feature/Footer';
import ArticlePartOne from './components/ArticlePartOne';
import ArticlePartTwo from './components/ArticlePartTwo';
import ArticlePartThree from './components/ArticlePartThree';
import ArticlePartFour from './components/ArticlePartFour';

const HowToPayPropertyTaxPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="How to Pay Property Taxes in El Paso TX | El Paso Tax Assessor-Collector Guide | TREX RE LLC"
        description="Complete guide to paying property taxes in El Paso County TX. Learn about online payment login, property tax search, tax rates, calculator, exemptions, and the El Paso Tax Assessor-Collector office. Pay property taxes easily."
        keywords="pay property taxes, property tax el paso county, property tax el paso calculator, el paso property tax search, El Paso Tax Assessor-Collector, El Paso property taxes payment online login, property tax el paso rate"
        canonical="/elpaso-tx/how-to-pay-property-tax"
        schema={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": "How to Pay Property Taxes in El Paso TX: Complete Guide to Rates, Search, Online Payment & Exemptions",
          "description": "Comprehensive guide covering every method to pay property taxes in El Paso County, including online login portal, in-person locations, tax rate breakdown, property tax calculator, search tools, and exemptions.",
          "author": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          },
          "publisher": {
            "@type": "Organization",
            "name": "TREX RE LLC"
          },
          "datePublished": "2025-01-20",
          "dateModified": new Date().toISOString().split('T')[0],
          "mainEntityOfPage": {
            "@type": "WebPage",
            "@id": "https://trexrealestate.com/elpaso-tx/how-to-pay-property-tax"
          }
        }}
      />
      <GlobalNavbar />

      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/afa8e3f163f0a4c6a03df478535e95c4.jpg"
            alt="El Paso TX skyline with Franklin Mountains showing residential neighborhoods where homeowners pay property taxes"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/60"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center w-full">
          <span className="inline-block px-4 py-2 bg-teal-600 text-white text-sm font-semibold rounded-full mb-6">
            El Paso Property Tax Guide
          </span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            How to Pay Property Taxes in El Paso TX
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto mb-6">
            Your complete guide to the El Paso Tax Assessor-Collector office, online payment portal, property tax search, rate breakdown, calculator, exemptions, and every payment method available to El Paso County homeowners.
          </p>
          <div className="flex items-center justify-center gap-4 text-white/80 text-sm">
            <span>By TREX RE LLC</span>
            <span>•</span>
            <span>January 2025</span>
            <span>•</span>
            <span>18 min read</span>
          </div>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="py-10 bg-gray-50 border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">In This Guide</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <a href="#tax-statement" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Understanding Your El Paso Tax Statement</span>
            </a>
            <a href="#tax-rate" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Property Tax Rate Breakdown & Calculator</span>
            </a>
            <a href="#online-payment" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Online Payment Login Step-by-Step</span>
            </a>
            <a href="#tax-search" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>El Paso Property Tax Search Tool</span>
            </a>
            <a href="#payment-methods" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>In-Person, Mail & Alternative Payments</span>
            </a>
            <a href="#exemptions" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Exemptions to Reduce Your Tax Bill</span>
            </a>
            <a href="#consequences" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Consequences of Not Paying Taxes</span>
            </a>
            <a href="#special-situations" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Military, Inherited & Rental Property Taxes</span>
            </a>
            <a href="#strategies" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>Smart Tax Management Strategies</span>
            </a>
            <a href="#faq" className="flex items-center text-gray-700 hover:text-teal-600 transition-colors cursor-pointer">
              <i className="ri-arrow-right-s-line text-teal-600 mr-2"></i>
              <span>FAQ & Contact Information</span>
            </a>
          </div>
        </div>
      </section>

      <ArticlePartOne />
      <ArticlePartTwo />
      <ArticlePartThree />
      <ArticlePartFour />

      <Footer />
    </div>
  );
};

export default HowToPayPropertyTaxPage;
